'use client';

import React from 'react';
import classnames from 'classnames';
import Box from '../../../atoms/Box/Box';
import Typography from '../../../atoms/Typography/Typography';

import styles from './LanguageToggle.module.scss';

/**
 * Map locale language codes to full native language names
 */
const LANGUAGE_NAMES: Record<string, string> = {
  en: 'English',
  da: 'Dansk',
  de: 'Deutsch',
  fr: 'Français',
  nl: 'Dutch',
  nb: 'Norsk',
  ar: 'العربية',
};

/**
 * Get the language code from a locale
 * e.g., 'de-de' -> 'de', 'fr-ch' -> 'fr'
 */
const getLanguageCode = (locale: string): string => {
  return locale.split('-')[0]?.toLowerCase() || 'en';
};

/**
 * Props for LanguageToggle component
 */
export type LanguageToggleProps = {
  /** Current theme */
  theme?: 'dark' | 'light';
  /** Current locale (e.g., 'de-de', 'en-us') */
  locale: string;
  /** Currently selected language code (null = native, 'en' = English override) */
  selectedLanguage: string | null;
  /** Callback when language selection changes */
  onLanguageChange: (languageCode: string | null) => void;
  /** i18n labels */
  labels?: {
    /** Label for the toggle section (default: "Language") */
    label?: string;
  };
};

/**
 * LanguageToggle - Select between available languages for UI labels
 *
 * @component
 * @param {LanguageToggleProps} props - Component props
 * @returns {React.ReactElement | null} The toggle component, or null if only one language available
 *
 * @example
 * <LanguageToggle
 *   theme="dark"
 *   locale="de-ch"
 *   selectedLanguage={null}
 *   onLanguageChange={(lang) => setLanguage(lang)}
 * />
 */
const LanguageToggle: React.FC<LanguageToggleProps> = ({
  theme = 'dark',
  locale,
  selectedLanguage,
  onLanguageChange,
  labels,
}) => {
  const nativeLanguageCode = getLanguageCode(locale);

  // Don't show toggle for English locales (already in English)
  if (nativeLanguageCode === 'en') {
    return null;
  }

  // Available languages: native language + English
  const availableLanguages = [nativeLanguageCode, 'en'];

  // Determine currently active language
  const activeLanguage = selectedLanguage || nativeLanguageCode;

  const handleLanguageClick = (langCode: string) => {
    // If selecting the native language, set to null (use locale default)
    // Otherwise, set the specific language override
    if (langCode === nativeLanguageCode) {
      onLanguageChange(null);
    } else {
      onLanguageChange(langCode);
    }
  };

  return (
    <Box>
      <Typography variant="label" size="small" color="gray-3" theme={theme}>
        {labels?.label ?? 'Language'}
      </Typography>
      <Box mt={4} className={styles.buttonGroup}>
        {availableLanguages.map((langCode) => (
          <button
            key={langCode}
            type="button"
            className={classnames(styles.languageButton, {
              [styles.active]: activeLanguage === langCode,
              [styles.inactive]: activeLanguage !== langCode,
            })}
            onClick={() => handleLanguageClick(langCode)}
            data-testid={`language-button-${langCode}`}
          >
            <Typography
              variant="body"
              size="small"
              color={activeLanguage === langCode ? 'white' : theme === 'dark' ? 'white' : 'black'}
              theme={theme}
            >
              {LANGUAGE_NAMES[langCode] || langCode.toUpperCase()}
            </Typography>
          </button>
        ))}
      </Box>
    </Box>
  );
};

LanguageToggle.displayName = 'LanguageToggle';

export default LanguageToggle;
